import React, { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { NoctuaMark } from '../common';
import { isAnalyticsAvailable } from '../../lib/analytics';
import { openConsentSettings } from '../../lib/consent';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  external?: boolean;
}

const baseNavItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/collection', label: 'Collection' },
  { path: '/feeds', label: 'Feeds' },
  { path: '/credits', label: 'Credits' },
  { path: '/settings', label: 'Settings' },
];

const adminNavItem: NavItem = { path: '/admin/reports', label: 'Inbox' };

export function Layout({ children }: LayoutProps) {
  const { user, signOut } = useAuth();
  const navItems = user?.isAdmin ? [...baseNavItems, adminNavItem] : baseNavItems;
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2" aria-label="noctua home">
              <NoctuaMark size={26} />
              <span className="font-display text-xl font-bold lowercase tracking-[-0.025em] text-foreground">
                noctua
                <sup className="font-medium text-muted-foreground tracking-normal ml-0.5 text-[0.5em] align-super">
                  ™
                </sup>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) =>
                item.external ? (
                  <a
                    key={item.path}
                    href={item.path}
                    className="inline-flex items-center min-h-[44px] px-4 py-2 rounded-sm font-body text-sm font-medium lowercase text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center min-h-[44px] px-4 py-2 rounded-sm font-body text-sm font-medium lowercase transition-colors ${
                      location.pathname === item.path
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-3">
                {user?.photoUrl && (
                  <img
                    src={user.photoUrl}
                    alt={user.displayName}
                    className="w-8 h-8 rounded-full border border-border"
                  />
                )}
                <span className="font-body text-sm text-foreground">{user?.displayName}</span>
              </div>
              <button
                onClick={handleSignOut}
                className="hidden md:inline-flex items-center min-h-[44px] px-2 font-body text-sm lowercase text-muted-foreground hover:text-foreground transition-colors"
              >
                Sign out
              </button>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-sm hover:bg-muted transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) =>
                item.external ? (
                  <a
                    key={item.path}
                    href={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 rounded-sm font-body text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center min-h-[44px] px-4 py-2 rounded-sm font-body text-sm font-medium lowercase ${
                      location.pathname === item.path
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
              <div className="pt-3 border-t border-border mt-3">
                <div className="flex items-center gap-3 px-4 py-2">
                  {user?.photoUrl && (
                    <img
                      src={user.photoUrl}
                      alt={user.displayName}
                      className="w-8 h-8 rounded-full border border-border"
                    />
                  )}
                  <span className="font-body text-sm text-foreground">{user?.displayName}</span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="w-full text-left min-h-[44px] px-4 py-2 font-body text-sm lowercase text-muted-foreground hover:bg-muted hover:text-foreground rounded-sm"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center gap-3 text-muted-foreground font-mono text-xs tracking-wider uppercase">
            <NoctuaMark size={14} />
            <span>noctua</span>
            <span className="w-1 h-1 rounded-full bg-muted-foreground opacity-50" />
            <span>{import.meta.env.VITE_APP_VERSION ?? 'dev'}</span>
            {isAnalyticsAvailable() && (
              <>
                <span className="w-1 h-1 rounded-full bg-muted-foreground opacity-50" />
                <button
                  type="button"
                  onClick={openConsentSettings}
                  className="font-mono text-xs tracking-wider uppercase text-muted-foreground hover:text-foreground transition-colors"
                >
                  cookie settings
                </button>
              </>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
